// src/rest/custom.ts

import { ethers } from 'ethers'

// Removed circular import - using type-only imports where needed
import type { Builder, UserOpenOrders } from '../types'
import type { CancelOrderRequest, OrderRequest, OrderResponse, OrderType } from '../types/index'
import type { CancelOrderResponse } from '../utils/signing'
import type { SymbolConversion } from '../utils/symbolConversion'
import type { ExchangeAPI } from './exchange'
import type { InfoAPI } from './info'

// Define minimal interface to avoid circular import
interface HyperliquidParent {
  exchange: ExchangeAPI
  info: InfoAPI
  symbolConversion: SymbolConversion
}

export class CustomOperations {
  private exchange: ExchangeAPI
  private infoApi: InfoAPI
  private wallet?: ethers.Wallet
  private symbolConversion: SymbolConversion
  private walletAddress: string | null
  private parent?: HyperliquidParent

  constructor(
    exchangeOrParent: ExchangeAPI | HyperliquidParent,
    infoApiOrPrivateKey?: InfoAPI | string,
    privateKeyOrSymbolConversion?: string | SymbolConversion,
    symbolConversionOrWalletAddress?: SymbolConversion | string | null,
    walletAddress?: string | null,
  ) {
    // Check if first argument is Hyperliquid instance (duck typing to avoid circular import)
    if ('exchange' in exchangeOrParent && 'info' in exchangeOrParent && 'symbolConversion' in exchangeOrParent) {
      this.parent = exchangeOrParent as HyperliquidParent
      this.exchange = exchangeOrParent.exchange
      this.infoApi = exchangeOrParent.info
      this.symbolConversion = exchangeOrParent.symbolConversion
      this.walletAddress = (exchangeOrParent as any).isAuthenticated?.()
        ? (exchangeOrParent as any).isAuthenticated().toString()
        : null
    }
    else {
      // Original constructor
      this.exchange = exchangeOrParent
      this.infoApi = infoApiOrPrivateKey as InfoAPI
      if (privateKeyOrSymbolConversion && typeof privateKeyOrSymbolConversion === 'string') {
        this.wallet = new ethers.Wallet(privateKeyOrSymbolConversion)
      }
      this.symbolConversion = symbolConversionOrWalletAddress as SymbolConversion
      this.walletAddress = walletAddress || null
    }
  }

  private getUserAddress(): string {
    if (!this.walletAddress && !this.wallet?.address) {
      throw new Error('No wallet address available. Please provide a wallet address or private key.')
    }
    return this.walletAddress || this.wallet!.address
  }

  async cancelAllOrders(symbol?: string): Promise<CancelOrderResponse> {
    try {
      const address = this.getUserAddress()
      const openOrders: UserOpenOrders = await this.infoApi.getUserOpenOrders(address)

      let ordersToCancel: UserOpenOrders

      for (const order of openOrders) {
        order.coin = await this.symbolConversion.convertSymbol(order.coin)
      }

      ordersToCancel = symbol ? openOrders.filter(order => order.coin === symbol) : openOrders

      if (ordersToCancel.length === 0) {
        throw new Error('No orders to cancel')
      }

      const cancelRequests: CancelOrderRequest[] = ordersToCancel.map(order => ({
        coin: order.coin,
        o: order.oid,
      }))

      const response = await this.exchange.cancelOrder(cancelRequests)
      return response
    }
    catch (error) {
      throw error
    }
  }

  async getAllAssets(): Promise<{ perp: string[], spot: string[] }> {
    return await this.symbolConversion.getAllAssets()
  }

  private DEFAULT_SLIPPAGE = 0.05

  private async getSlippagePrice(symbol: string, isBuy: boolean, slippage: number, px?: number): Promise<number> {
    const convertedSymbol = await this.symbolConversion.convertSymbol(symbol)
    if (!px) {
      const allMids = await this.infoApi.getAllMids()
      px = Number(allMids[convertedSymbol])
    }

    const isSpot = symbol.includes('-SPOT')

    // If not isSpot count how many decimals price has to use the same amount for rounding
    const decimals = px.toString().split('.')[1]?.length || 0

    console.log(decimals)

    px *= isBuy ? 1 + slippage : 1 - slippage
    return Number(px.toFixed(isSpot ? 8 : Math.max(0, decimals - 1)))
  }

  async marketOpen(
    symbol: string,
    isBuy: boolean,
    size: number,
    px: number,
    slippage: number = this.DEFAULT_SLIPPAGE,
    cloid?: string,
    builder?: Builder,
  ): Promise<OrderResponse> {
    const convertedSymbol = await this.symbolConversion.convertSymbol(symbol)
    const slippagePrice = await this.getSlippagePrice(convertedSymbol, isBuy, slippage, px)
    console.log('Slippage Price:', slippagePrice)

    const orderRequest: OrderRequest = {
      coin: convertedSymbol,
      is_buy: isBuy,
      sz: size,
      limit_px: slippagePrice,
      order_type: { limit: { tif: 'Ioc' } } as OrderType,
      reduce_only: false,
      builder,
    }

    if (cloid) {
      orderRequest.cloid = cloid
    }
    console.log(orderRequest)
    return this.exchange.placeOrder(orderRequest)
  }

  async marketClose(
    symbol: string,
    szi: string,
    px: number,
    slippage: number = this.DEFAULT_SLIPPAGE,
    cloid?: string,
    builder?: Builder,
  ): Promise<OrderResponse> {
    const convertedSymbol = await this.symbolConversion.convertSymbol(symbol)

    const closeSize = Math.abs(Number.parseFloat(szi))
    const isBuy = Number.parseFloat(szi) < 0

    // Get aggressive Market Price
    const slippagePrice = await this.getSlippagePrice(convertedSymbol, isBuy, slippage, px)

    // Market Order is an aggressive Limit Order IoC
    const orderRequest: OrderRequest = {
      coin: convertedSymbol,
      is_buy: isBuy,
      sz: closeSize,
      limit_px: slippagePrice,
      order_type: { limit: { tif: 'Ioc' } } as OrderType,
      reduce_only: true,
      builder,
    }

    if (cloid) {
      orderRequest.cloid = cloid
    }

    return this.exchange.placeOrder(orderRequest)
  }

  async limitOpen(
    symbol: string,
    isBuy: boolean,
    size: number,
    limitPrice: number,
    tif: 'Gtc' | 'Alo' | 'Ioc' = 'Gtc',
    cloid?: string,
    builder?: Builder,
  ): Promise<OrderResponse> {
    const convertedSymbol = await this.symbolConversion.convertSymbol(symbol)

    const orderRequest: OrderRequest = {
      coin: convertedSymbol,
      is_buy: isBuy,
      sz: size,
      limit_px: limitPrice,
      order_type: { limit: { tif } } as OrderType,
      reduce_only: false,
      builder,
    }

    if (cloid) {
      orderRequest.cloid = cloid
    }

    console.log('Limit Open Order:', orderRequest)
    return this.exchange.placeOrder(orderRequest)
  }

  async limitClose(
    symbol: string,
    limitPrice: number,
    szi: string,
    tif: 'Gtc' | 'Alo' | 'Ioc' = 'Gtc',
    cloid?: string,
    builder?: Builder,
  ): Promise<OrderResponse> {
    const convertedSymbol = await this.symbolConversion.convertSymbol(symbol)
    const closeSize = Math.abs(Number.parseFloat(szi))
    const isBuy = Number.parseFloat(szi) < 0

    const orderRequest: OrderRequest = {
      coin: convertedSymbol,
      is_buy: isBuy,
      sz: closeSize,
      limit_px: limitPrice,
      order_type: { limit: { tif } } as OrderType,
      reduce_only: true,
      builder,
    }

    if (cloid) {
      orderRequest.cloid = cloid
    }

    console.log('Limit Close Order:', orderRequest)
    return this.exchange.placeOrder(orderRequest)
  }

  // async closeAllPositions(slippage: number = this.DEFAULT_SLIPPAGE): Promise<OrderResponse[]> {
  //   try {
  //     const address = this.getUserAddress()
  //     const positions = await this.infoApi.perpetuals.getClearinghouseState(address)
  //     const closeOrders: Array<Promise<OrderResponse>> = []

  //     console.log(positions)

  //     for (const position of positions.assetPositions) {
  //       const item = position.position
  //       if (Number.parseFloat(item.szi) !== 0) {
  //         const symbol = await this.symbolConversion.convertSymbol(item.coin, 'forward')
  //         closeOrders.push(this.marketClose(symbol, undefined, undefined, slippage))
  //       }
  //     }

  //     return await Promise.all(closeOrders)
  //   } catch (error) {
  //     throw error
  //   }
  // }
}
